import type { Action, HandState } from "@joker-holdem/core";
import { BetControls } from "./BetControls.js";
import { clampToLegalRange, presetsFor } from "../engine/betPresets.js";
import type { BetAmountState } from "../engine/useBetAmount.js";
import { formatBb } from "../engine/selectors.js";

interface ActionBarProps {
  hand: HandState;
  /** Lifted from `useBetAmount`, called once in `PlayScreen` -- the amount
   * input itself now lives in the hero's `PlayerPanel` (2026-07-17
   * フィードバック #3), so both need this same state instead of each owning
   * their own copy. */
  betAmount: BetAmountState;
  onAction: (action: Action) => void;
}

export function ActionBar({ hand, betAmount, onAction }: ActionBarProps) {
  const { legal, fold, check, call, open, betValue, presetCtx, setBetValue } = betAmount;

  if (legal.length === 0) return null;

  return (
    <div className="action-bar">
      {open && (
        <BetControls
          presetCtx={presetCtx}
          min={open.min}
          max={open.max}
          value={betValue}
          onChange={setBetValue}
        />
      )}
      <div className="action-buttons">
        <button
          type="button"
          className="action-btn action-btn-fold"
          disabled={!fold}
          onClick={() => fold && onAction({ type: "fold" })}
        >
          Fold
        </button>
        <button
          type="button"
          className="action-btn action-btn-call"
          disabled={!check && !call}
          onClick={() => {
            if (check) onAction({ type: "check" });
            else if (call) onAction({ type: "call" });
          }}
        >
          {call ? `Call ${formatBb(call.amount)}` : "Check"}
        </button>
        <button
          type="button"
          className="action-btn action-btn-raise"
          disabled={!open}
          onClick={() => {
            if (!open) return;
            const to = clampToLegalRange(open.min, open.max, betValue);
            onAction({ type: open.type, to });
          }}
        >
          {open
            ? `${open.type === "bet" ? "Bet" : "Raise"} ${formatBb(clampToLegalRange(open.min, open.max, betValue))}`
            : "Bet"}
        </button>
      </div>
    </div>
  );
}

// Re-export so consumers of ActionBar can build a matching presets preview elsewhere if needed.
export { presetsFor };
